import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import styled from "styled-components";

import { useFleetAuth } from "../../auth/fleetAuth";
import TractorState from "./TractorState";

/**
 * Tractor State on the APP-WIDE session, for builds where the whole console
 * already authenticates against the platform_auth Cognito pool.
 *
 * The page-scoped login (`TractorStateLive` + `FleetStateAuthProvider`) exists
 * because this page needed a Cognito token while the rest of the app was still
 * on Auth0. Once the app itself is on Cognito -- the SAME pool and app client --
 * that second login is not merely redundant, it actively breaks:
 *
 *   Two `AuthProvider`s were mounted, one storing state in sessionStorage and
 *   one in localStorage. The page-scoped provider found nothing in its own store
 *   and began a sign-in; Cognito, already holding a session, returned a code to
 *   /tractor-state; and the ROOT provider processed that `?code=` first, did not
 *   recognise a state belonging to the other provider, and failed the sign-in
 *   with "No matching state found in storage".
 *
 * So on the Cognito path this component replaces that subtree entirely. One
 * session, one login, and no second provider to race with. It is also what makes
 * `/tractor-state` removable from both pools' callback lists, which SW-6943
 * asked for.
 *
 * `RequireAuth` and `RequirePermission` still wrap this route, so a session and
 * `read:aerovect-vehicles` are already established before it renders; the wait
 * below is for the token read, not for the login.
 */
export default function TractorStateAppSession() {
  const { getAccessToken } = useFleetAuth();
  const [token, setToken] = useState<string | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAccessToken().then(
      (t) => !cancelled && setToken(t),
      // getAccessToken throws rather than resolving undefined, so a missing
      // session lands here instead of reaching the API as "Bearer undefined".
      () => !cancelled && setFailed(true),
    );
    return () => {
      cancelled = true;
    };
  }, [getAccessToken]);

  if (failed) {
    // Deliberately not a redirect to a login: RequireAuth above already
    // established one, so arriving here means the token read failed rather than
    // that the user is signed out. Say so instead of looping them.
    return (
      <Centered>
        <Text>
          Couldn’t read your session token. Reload the page, or sign out and
          back in.
        </Text>
      </Centered>
    );
  }

  if (!token) {
    return (
      <Centered>
        <CircularProgress size={40} />
        <Text>Loading…</Text>
      </Centered>
    );
  }

  return <TractorState token={token} />;
}

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  gap: 16px;
`;

const Text = styled.p`
  color: ${(props) => props.theme.colors.grey};
  font-size: 14px;
`;
